/**
 * Log channels — THE source of truth.
 *
 * A process's streams are registered on the continuum.stream ledger, one row per
 * channel. This table says, for each channel we know: is it a log channel at all,
 * what BufferSource its records declare, which parsers may be applied to it, and
 * which one to use by default.
 *
 * Everything else derives from here, and nothing re-states it:
 *   - the channel chooser lists the run's channels that are LOG channels
 *   - the parser picker offers exactly `parsers`, and disables itself when there
 *     is only one (nothing to choose)
 *   - resolveParserType validates a requested parser against `parsers`
 *   - LogParsingPipeline routes a buffer by its declared source
 *
 * Before this table those facts lived in five places (two copies of the rpc-channel
 * set, a channel->default-parser map, a separate structured-channel set, and the
 * pipeline's own hardcoded source checks) and could disagree — which is how
 * ?parser=journal came to show "journal" in the picker while applog did the
 * rendering.
 *
 * THE LOAD-BEARING RULE, and the reason this table is keyed the way it is:
 * a buffer that DECLARES a source is routed by that source, and the parser choice
 * applies only to buffers that declare none (BufferSource.unspecified — plain
 * stdout/stderr text). So a channel whose records always declare a source has no
 * meaningful parser choice: `parsers` is a single value, and the picker goes quiet.
 */

import { BufferSource } from '../../hermesclient/proto/continuum_rpc';
// Type-only: LogParserType is erased at runtime, so this does not create a module
// cycle with types.ts (which imports channelDef from here for resolveParserType).
import type { LogParserType } from './types';

/** What a channel's records are made of — and therefore what can be done with them. */
export type ChannelKind =
  /** Log records. Viewable; the parser picker applies per `parsers` below. */
  | 'log'
  /** A mesh RPC mailbox. A stream on the ledger, but it carries RPC envelopes, not
   *  logs — never offered as a log channel. Mirrors mesh/api.RpcChannels. */
  | 'rpc';

export interface ChannelDef {
  kind: ChannelKind;
  /**
   * The BufferSource this channel's records declare, when they declare one.
   * The pipeline routes on the buffer's OWN source (never on the channel name — the
   * buffer is the thing that knows); this field is what ties the two together, so a
   * channel's `parsers` below can't drift from what the pipeline will actually do.
   *
   * undefined => the records declare no source (unspecified), i.e. captured
   * stdout/stderr text. Those are the buffers a parser choice actually governs.
   */
  bufferSource?: BufferSource;
  /**
   * Every parser that may be applied. A single entry means the rendering is a fact,
   * not a preference — the picker disables itself rather than accepting a choice the
   * pipeline is going to ignore.
   */
  parsers: LogParserType[];
  /** The one to use when nothing else (URL, job config) picks. Must be in `parsers`. */
  defaultParser: LogParserType;
}

// The line-oriented parsers. Only a channel whose buffers declare NO source can be
// read with a choice of these — see the rule in the file header, and CHANNELS below.
const TEXT_PARSERS: LogParserType[] = ['structured', 'plain-text', 'journal', 'eventlog'];

const CHANNELS: Record<string, ChannelDef> = {
  // stdout/stderr captured from the outside: free-form text, and the buffers declare
  // no source the pipeline special-cases (unspecified/stdout/stderr). This is THE
  // channel the parser picker exists for — the only one where the choice does anything.
  console: {
    kind: 'log',
    parsers: TEXT_PARSERS,
    defaultParser: 'structured',
  },

  // The process's OWN logger, shipped as LogRecord protos: level, caller, thread and
  // the exception are FIELDS on the wire. Buffer.record is set, Buffer.data is EMPTY —
  // there is no text for any parser to read (godev applog/spool.go).
  applog: {
    kind: 'log',
    bufferSource: BufferSource.applog,
    parsers: ['applog'],
    defaultParser: 'applog',
  },

  // systemd journald: one JSON record per buffer, and the buffers DECLARE
  // journalctl (godev pkg/servicemanager/agent/journal.go), so the pipeline routes
  // them to the journal parser unconditionally. The records are text, so another
  // parser is imaginable — but it would not run. Listing one here would recreate the
  // applog bug on a different channel: a choice offered, accepted, and ignored.
  journal: {
    kind: 'log',
    bufferSource: BufferSource.journalctl,
    parsers: ['journal'],
    defaultParser: 'journal',
  },

  // Windows Event Log: same story as journal — the buffers declare their source
  // (pkg/servicemanager/agent/eventlog.go), so the routing is fixed.
  eventlog: {
    kind: 'log',
    bufferSource: BufferSource.windows_event_log,
    parsers: ['eventlog'],
    defaultParser: 'eventlog',
  },

  // The mesh RPC mailboxes. Streams on the ledger, but not logs.
  'rpc-inbox': { kind: 'rpc', parsers: [], defaultParser: 'structured' },
  'rpc-sent': { kind: 'rpc', parsers: [], defaultParser: 'structured' },
};

// A channel nobody has declared is assumed to be captured text — the same treatment
// as console. This is deliberately permissive: a NEW log channel shows up and works,
// rather than being invisible until someone remembers to add it here.
const UNKNOWN_CHANNEL: ChannelDef = {
  kind: 'log',
  parsers: TEXT_PARSERS,
  defaultParser: 'structured',
};

export function channelDef(channel: string | undefined): ChannelDef {
  if (!channel) return UNKNOWN_CHANNEL;
  return CHANNELS[channel] ?? UNKNOWN_CHANNEL;
}

/** Is this a channel a person can read logs from? (false for the rpc mailboxes.) */
export function isLogChannel(channel: string): boolean {
  return channelDef(channel).kind === 'log';
}

/**
 * The parser LogParsingPipeline will actually use for a buffer that DECLARES this
 * source — i.e. what the reader is going to see, regardless of what anyone selected.
 *
 * This is the bridge between the table and the pipeline, and it exists to be
 * ASSERTED against (see channels.test.ts): the pipeline rightly routes on the
 * buffer's own source rather than on the channel name (the buffer is the thing that
 * knows what it holds), so the table must not claim a channel offers parsers that
 * its buffers' source will override. Undefined for sources the pipeline does not
 * special-case (unspecified/stdout/stderr) — those ARE governed by the parser choice.
 */
export function parserForBufferSource(source: BufferSource): LogParserType | undefined {
  switch (source) {
    case BufferSource.applog:
      return 'applog';
    case BufferSource.journalctl:
      return 'journal';
    case BufferSource.windows_event_log:
      return 'eventlog';
    default:
      return undefined; // unspecified / stdout / stderr — the parser choice applies
  }
}

/** The parsers the picker should offer for this channel. */
export function parsersForChannel(channel: string | undefined): LogParserType[] {
  return channelDef(channel).parsers;
}

/**
 * True when the channel admits exactly one parser, so there is nothing to choose and
 * the picker should be inert. Derived — NOT a second list to keep in sync.
 */
export function parserIsFixed(channel: string | undefined): boolean {
  return parsersForChannel(channel).length <= 1;
}
